/**
 * ConflictResolutionModal - Manual conflict resolution UI for complex conflicts
 */
import React, { useState } from 'react';
import { AlertTriangle, Check, X, RefreshCw } from 'lucide-react';
import { showToast } from '../utils/toast';

const ConflictResolutionModal = ({ operation, onResolve, onClose }) => {
  const [selectedResolution, setSelectedResolution] = useState(null);
  const [isResolving, setIsResolving] = useState(false);

  if (!operation) return null;

  const handleResolve = async () => {
    if (!selectedResolution) {
      showToast('Please select a resolution option', 'error');
      return;
    }

    setIsResolving(true);
    try {
      await onResolve(operation?.id, selectedResolution);
      showToast('Conflict resolved successfully', 'success');
      onClose();
    } catch (error) {
      showToast('Failed to resolve conflict', 'error');
    } finally {
      setIsResolving(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const getOperationLabel = (type) => {
    const labels = {
      bookmark: 'Bookmark',
      clap: 'Clap',
      comment: 'Comment',
      story_update: 'Story Update',
    };
    return labels?.[type] || type;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Resolve Conflict</h2>
              <p className="text-sm text-muted-foreground">
                {getOperationLabel(operation?.operationType)} operation requires manual resolution
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conflict Details */}
        <div className="p-6 space-y-6">
          {/* Operation Info */}
          <div className="bg-muted rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Operation Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type:</span>
                <span className="text-foreground font-medium">{getOperationLabel(operation?.operationType)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Queued At:</span>
                <span className="text-foreground">{formatTimestamp(operation?.queuedAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Retry Count:</span>
                <span className="text-foreground">{operation?.retryCount}</span>
              </div>
            </div>
          </div>

          {/* Conflict Information */}
          {operation?.conflictDetails && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Conflict Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Conflict Type:</span>
                  <span className="text-foreground font-medium">{operation?.conflictDetails?.type}</span>
                </div>
                {operation?.conflictDetails?.queuedTime && operation?.conflictDetails?.serverTime && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Queued Version:</span>
                      <span className="text-foreground">{formatTimestamp(operation?.conflictDetails?.queuedTime)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Server Version:</span>
                      <span className="text-foreground">{formatTimestamp(operation?.conflictDetails?.serverTime)}</span>
                    </div>
                  </>
                )}
                {operation?.conflictDetails?.conflictingFields && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Conflicting Fields:</span>
                    <span className="text-foreground">{operation?.conflictDetails?.conflictingFields?.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resolution Options */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Choose Resolution</h3>
            <div className="space-y-3">
              {/* Use Queued Data */}
              <button
                onClick={() => setSelectedResolution('use_queued')}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  selectedResolution === 'use_queued' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    selectedResolution === 'use_queued' ?'border-primary bg-primary' :'border-muted-foreground'
                  }`}>
                    {selectedResolution === 'use_queued' && (
                      <Check className="w-3 h-3 text-primary-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">Use My Changes</p>
                    <p className="text-sm text-muted-foreground">
                      Apply the queued operation and overwrite server data. Use this if your local changes are more recent or correct.
                    </p>
                  </div>
                </div>
              </button>

              {/* Use Server Data */}
              <button
                onClick={() => setSelectedResolution('use_server')}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  selectedResolution === 'use_server' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    selectedResolution === 'use_server' ?'border-primary bg-primary' :'border-muted-foreground'
                  }`}>
                    {selectedResolution === 'use_server' && (
                      <Check className="w-3 h-3 text-primary-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">Keep Server Version</p>
                    <p className="text-sm text-muted-foreground">
                      Discard the queued operation and keep the current server data. Use this if the server has more recent changes.
                    </p>
                  </div>
                </div>
              </button>

              {/* Skip Operation */}
              <button
                onClick={() => setSelectedResolution('skip')}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  selectedResolution === 'skip' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    selectedResolution === 'skip' ?'border-primary bg-primary' :'border-muted-foreground'
                  }`}>
                    {selectedResolution === 'skip' && (
                      <Check className="w-3 h-3 text-primary-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-1">Skip This Operation</p>
                    <p className="text-sm text-muted-foreground">
                      Remove this operation from the queue without applying it. Use this if the operation is no longer relevant.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            disabled={isResolving}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={!selectedResolution || isResolving}
            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isResolving && <RefreshCw className="w-4 h-4 animate-spin" />}
            {isResolving ? 'Resolving...' : 'Apply Resolution'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;